import { useState, useRef, useEffect, useCallback } from "react";
import {
  usePhotosQuery,
  useUploadPhoto,
  useReplacePhoto,
  useDeletePhoto,
  useSetCoverPhoto,
  useRemoveCoverPhoto,
} from "../hooks/usePhotosQuery.js";
import { prepareForUpload, isTouchDevice } from "../utility/imageUpload.js";
import { useCachedPhoto } from "../hooks/useCachedPhoto.js";

// Small inline spinner (the shared Spinner is a full-screen h-screen loader, unsuitable here).
function MiniSpinner({ className = "h-4 w-4 text-white" }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// Human-readable message for an upload/replace error, mapping known API statuses.
function uploadErrorMessage(err) {
  if (!err) return null;
  if (err.status === 402) {
    const limit = err.body?.limit;
    return limit != null
      ? `Photo limit reached (${limit}) for your plan. Upgrade or delete a photo to add more.`
      : "Photo limit reached for your plan.";
  }
  if (err.status === 415) return "Unsupported format — please upload a JPEG or PNG.";
  if (err.status === 413) return "Photo is larger than 15 MB.";
  if (err.status === 503) return "Photo storage isn't configured yet.";
  return err.message || "Upload failed.";
}

// Lightbox modal: full-size image, Esc to close, focus trap on the close button.
export function Lightbox({ photo, onClose }) {
  const closeRef = useRef(null);
  // revalidate: false — full-res images are large (up to 2048px) and only viewed
  // on-demand, so we show the cached copy if present (or fetch once) without an extra
  // background re-fetch every time the lightbox opens.
  const cachedFullUrl = useCachedPhoto(photo?.full_url, { revalidate: false });

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!photo) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={photo.caption || "Photo"}
    >
      <div className="relative max-h-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <img
          src={cachedFullUrl || photo.full_url}
          alt={photo.caption || "Photo"}
          className="max-h-[85vh] w-auto rounded-lg object-contain"
        />
        {photo.caption && (
          <p className="mt-2 text-center text-sm text-gray-200">{photo.caption}</p>
        )}
        <button
          ref={closeRef}
          onClick={onClose}
          className="absolute -right-3 -top-3 rounded-full bg-white p-1.5 text-gray-700 shadow hover:bg-gray-100"
          aria-label="Close"
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function PhotoTile({ photo, resource, id, onOpen }) {
  const replace = useReplacePhoto(resource, id);
  const del = useDeletePhoto(resource, id);
  const setCover = useSetCoverPhoto(resource, id);
  const removeCover = useRemoveCoverPhoto(resource, id);
  const cachedThumbUrl = useCachedPhoto(photo.thumbnail_url);
  const replaceInput = useRef(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [err, setErr] = useState(null);

  const busy = replace.isPending || del.isPending || setCover.isPending || removeCover.isPending;

  async function onReplaceFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setErr(null);
    try {
      const prepared = await prepareForUpload(file);
      await replace.mutateAsync({ photoId: photo.id, file: prepared });
    } catch (ex) {
      setErr(uploadErrorMessage(ex.status ? ex : { message: ex.message }));
    }
  }

  return (
    <div className="group relative aspect-square overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
      <button
        type="button"
        onClick={() => onOpen(photo)}
        className="h-full w-full"
        aria-label={photo.caption ? `Open photo: ${photo.caption}` : "Open photo"}
      >
        {photo.thumbnail_url ? (
          <img
            src={cachedThumbUrl || photo.thumbnail_url}
            alt={photo.caption || "Photo"}
            className="h-full w-full object-cover transition group-hover:opacity-90"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-400">📷</div>
        )}
      </button>

      {photo.is_cover && (
        <span className="absolute left-1 top-1 flex items-center gap-1 rounded bg-emerald-600/90 py-0.5 pl-1.5 pr-1 text-[10px] font-medium text-white">
          Cover
          <button
            type="button"
            onClick={() => removeCover.mutate()}
            disabled={busy}
            className="rounded-full px-1 leading-none hover:bg-white/20 disabled:opacity-40"
            title="Remove cover photo"
            aria-label="Remove cover photo"
          >
            ×
          </button>
        </span>
      )}

      {/* Action bar — visible on hover (desktop) / always on touch */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1 bg-black/45 p-1 opacity-0 transition group-hover:opacity-100 sm:opacity-0 [@media(pointer:coarse)]:opacity-100">
        {busy && <MiniSpinner />}
        {!confirmDelete ? (
          <>
            <button
              type="button"
              onClick={() => setCover.mutate(photo.id)}
              disabled={busy || photo.is_cover}
              className="rounded px-1.5 py-0.5 text-xs text-white hover:bg-white/20 disabled:opacity-40"
              title="Set as cover photo"
            >
              Cover
            </button>
            <button
              type="button"
              onClick={() => replaceInput.current?.click()}
              disabled={busy}
              className="rounded px-1.5 py-0.5 text-xs text-white hover:bg-white/20 disabled:opacity-40"
              title="Replace this photo"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              className="rounded px-1.5 py-0.5 text-xs text-white hover:bg-white/20 disabled:opacity-40"
            >
              Delete
            </button>
          </>
        ) : (
          <>
            <span className="mr-auto pl-1 text-xs text-white">Delete?</span>
            <button
              type="button"
              onClick={() => del.mutate(photo.id)}
              disabled={busy}
              className="rounded bg-red-600 px-1.5 py-0.5 text-xs text-white hover:bg-red-500"
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="rounded px-1.5 py-0.5 text-xs text-white hover:bg-white/20"
            >
              No
            </button>
          </>
        )}
      </div>

      <input
        ref={replaceInput}
        type="file"
        accept={isTouchDevice() ? "image/*" : "image/jpeg,image/png"}
        className="hidden"
        onChange={onReplaceFile}
      />

      {err && (
        <p className="absolute inset-x-0 top-0 bg-red-600/90 p-1 text-center text-[10px] text-white">{err}</p>
      )}
    </div>
  );
}

// Direct "Add photo" upload control — used by inventory/recipe galleries, which have no
// notes/events timeline to upload through (unlike grows, where photos are added via the
// Notes composer and this control is intentionally omitted).
function AddPhotoButton({ resource, id }) {
  const upload = useUploadPhoto(resource, id);
  const fileInput = useRef(null);
  const [err, setErr] = useState(null);

  async function onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(null);
    try {
      const prepared = await prepareForUpload(file);
      await upload.mutateAsync({ file: prepared });
    } catch (ex) {
      setErr(uploadErrorMessage(ex.status ? ex : { message: ex.message }));
    }
  }

  return (
    <div className="flex items-center gap-2">
      {err && <span className="text-xs text-red-600">{err}</span>}
      <button
        type="button"
        onClick={() => fileInput.current?.click()}
        disabled={upload.isPending}
        className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
      >
        {upload.isPending ? "Uploading…" : "Add photo"}
      </button>
      <input
        ref={fileInput}
        type="file"
        accept={isTouchDevice() ? "image/*" : "image/jpeg,image/png"}
        className="hidden"
        onChange={onFile}
      />
    </div>
  );
}

// resource: "grows" | "inventory" | "recipes". Grows upload only via the Notes composer
// (no AddPhotoButton here — that flow is deliberate, see GrowNotesFeed.jsx); inventory
// and recipes get a direct upload button since neither has a notes timeline.
export default function PhotoGallery({ resource, id }) {
  const { data: photos = [], isLoading } = usePhotosQuery(resource, id);
  const [lightbox, setLightbox] = useState(null);
  const showDirectUpload = resource !== "grows";

  const onOpen = useCallback((photo) => setLightbox(photo), []);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Photos {photos.length > 0 && <span className="text-gray-400">({photos.length})</span>}
        </h3>
        {showDirectUpload ? (
          <AddPhotoButton resource={resource} id={id} />
        ) : (
          <span className="text-xs text-gray-400">Add photos from Notes &amp; Events below</span>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6"><MiniSpinner className="h-6 w-6 text-gray-400" /></div>
      ) : photos.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">
          {showDirectUpload
            ? <>No photos yet. Click <span className="font-medium">Add photo</span> above to upload one.</>
            : <>No photos yet. Add one from <span className="font-medium">Notes &amp; Events</span> below —
                write a note, choose <span className="font-medium">Photo taken</span>, and attach a photo.</>}
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
          {photos.map((p) => (
            <PhotoTile key={p.id} photo={p} resource={resource} id={id} onOpen={onOpen} />
          ))}
        </div>
      )}

      <Lightbox photo={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
